'use client';
import React, { ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import Sidebar from '@/components/Sidebar';
import ClientSidebar from '@/components/ClientSidebar';
import AdminSidebar from '@/components/AdminSidebar';
import NotificationBell from '@/components/NotificationBell';
import ThemeToggle from '@/components/ThemeToggle';

function hasNoSidebar(node: ReactNode): boolean {
  let found = false;
  React.Children.forEach(node, (child) => {
    if (found) return;
    if (React.isValidElement(child)) {
      if ((child.type as any)?.noSidebar) {
        found = true;
        return;
      }
      if (hasNoSidebar(child.props.children)) {
        found = true;
        return;
      }
    }
  });
  return found;
}

interface SidebarWrapperProps {
  children: ReactNode;
}

export default function SidebarWrapper({ children }: SidebarWrapperProps) {
  const pathname = usePathname() || '';
  if (hasNoSidebar(children)) {
    return <>{children}</>;
  }
  const isAuthRoute =
    pathname === '/login' || pathname === '/auth/register';
  if (isAuthRoute) {
    return <>{children}</>;
  }

  // Homepage (marketing page) - no sidebar
  if (pathname === '/') {
    return <>{children}</>;
  }

  const isClientRoute = pathname.startsWith('/client');
  const isAdminRoute = pathname.startsWith('/admin');
  const isFleetRoute = pathname.startsWith('/fleet');

  // Fleet is a separate platform with its own layout; render without global sidebars/nav
  if (isFleetRoute) {
    return <>{children}</>;
  }

  let SidebarComponent = Sidebar;
  if (isClientRoute) {
    SidebarComponent = ClientSidebar;
  } else if (isAdminRoute) {
    SidebarComponent = AdminSidebar;
  }

  return (
    <div className="flex min-h-screen bg-background">
      <NotificationBell />
      <SidebarComponent />
      <main className="flex-1 container">
        <div className="flex justify-end py-3">
          <ThemeToggle />
        </div>
        {children}
      </main>
    </div>
  );
}